/**
 * Spectator Boosts Admin Page
 * View all spectator boost logs for Spades matches
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, RefreshCw, DollarSign, Users, Search } from 'lucide-react';

// Emerald Icon Component
const EmeraldIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="emeraldGradientBoosts" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#34d399" />
        <stop offset="50%" stopColor="#10b981" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
    </defs>
    <path d="M12 2L4 7V17L12 22L20 17V7L12 2Z" fill="url(#emeraldGradientBoosts)" />
    <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#6ee7b7" fillOpacity="0.6" />
    <path d="M12 12V22L4 17V7L12 12Z" fill="#059669" fillOpacity="0.4" />
  </svg>
);

// Ruby Icon Component
const RubyIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="rubyGradientBoosts" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f87171" />
        <stop offset="50%" stopColor="#ef4444" />
        <stop offset="100%" stopColor="#dc2626" />
      </linearGradient>
    </defs>
    <path d="M12 2L4 7V17L12 22L20 17V7L12 2Z" fill="url(#rubyGradientBoosts)" />
    <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#fca5a5" fillOpacity="0.6" />
    <path d="M12 12V22L4 17V7L12 12Z" fill="#dc2626" fillOpacity="0.4" />
  </svg>
);

import { API_BASE_URL } from '../../config';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const SpectatorBoostsPage = () => {
  const [boosts, setBoosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_count: 0,
    total_emeralds: 0,
    total_platform_cut: 0
  });
  const [searchQuery, setSearchQuery] = useState('');

  const fetchBoosts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/spectator-boosts');
      setBoosts(response.data.boosts || []);
      setStats({
        total_count: response.data.total_count || 0,
        total_emeralds: response.data.total_emeralds || 0,
        total_platform_cut: response.data.total_platform_cut || 0
      });
    } catch (error) {
      console.error('Failed to fetch boosts:', error);
      toast.error('Failed to load spectator boosts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoosts();
  }, []);

  const filteredBoosts = boosts.filter(boost => 
    boost.viewer_gems_tag?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    boost.match_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <EmeraldIcon className="h-6 w-6" /> Spectator Boost Logs
          </h1>
          <p className="text-gray-600">Track spectator contributions to Spades match prize pools</p>
        </div>
        <button
          onClick={fetchBoosts}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Boosts</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_count}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Emeralds Boosted</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.total_emeralds.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Platform Cut (15%)</p>
              <p className="text-2xl font-bold text-amber-600">{stats.total_platform_cut.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by GEMS Tag or Match ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      </div>

      {/* Boosts Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Match ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Viewer GEMS Tag</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount (Emeralds)</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Rubies Added</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Platform Cut</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    Loading boosts...
                  </td>
                </tr>
              ) : filteredBoosts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No spectator boosts found
                  </td>
                </tr>
              ) : (
                filteredBoosts.map((boost) => (
                  <tr key={boost.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-600">
                      {boost.match_id?.slice(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-emerald-600">
                      @{boost.viewer_gems_tag}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 flex items-center gap-1">
                      <EmeraldIcon className="h-4 w-4" /> {boost.amount_emeralds}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-red-600">
                      <span className="flex items-center gap-1"><RubyIcon className="h-4 w-4" /> {boost.amount_rubies}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-amber-600">
                      {boost.platform_cut?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(boost.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SpectatorBoostsPage;
